import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, Target, CheckCircle, Clock, TrendingUp, FileText, Play, Pause, Sparkle, Wand, TrendingDown, FileText as FileTextIcon } from 'lucide-react';

interface ComprehensionQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
}

interface LearningProgress {
  userId: string;
  articleId: string;
  readingSpeed: number;
  comprehensionScore: number;
  timeSpent: number;
  sectionsCompleted: number;
  totalSections: number;
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced';
  adaptiveSuggestions: string[];
  lastUpdated: Date;
}

interface LearningModeProps {
  userId: string;
  articleId: string;
  content: string;
  onContentAdapt?: (adaptedContent: string) => void;
  onComprehensionCheck?: (check: ComprehensionQuestion) => void;
  onLearningProgress?: (progress: LearningProgress) => void;
}

const LearningMode: React.FC<LearningModeProps> = ({
  userId,
  articleId,
  content,
  onContentAdapt,
  onComprehensionCheck,
  onLearningProgress
}) => {
  const [isActive, setIsActive] = useState(false);
  const [currentDifficulty, setCurrentDifficulty] = useState<'beginner' | 'intermediate' | 'advanced'>('intermediate');
  const [readingSpeed, setReadingSpeed] = useState(250); // words per minute
  const [comprehensionScore, setComprehensionScore] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [sectionsCompleted, setSectionsCompleted] = useState(0);
  const [totalSections, setTotalSections] = useState(1);
  const [adaptiveSuggestions, setAdaptiveSuggestions] = useState<string[]>([]);
  const [showComprehensionCheck, setShowComprehensionCheck] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<ComprehensionQuestion | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    if (isActive) {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setTimeSpent(Date.now() - startTimeRef.current);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      // Simulate adaptive learning
      const suggestions = generateAdaptiveSuggestions();
      setAdaptiveSuggestions(suggestions);
      
      // Update progress
      const progress: LearningProgress = {
        userId,
        articleId,
        readingSpeed,
        comprehensionScore,
        timeSpent,
        sectionsCompleted,
        totalSections,
        difficultyLevel: currentDifficulty,
        adaptiveSuggestions: suggestions,
        lastUpdated: new Date()
      };
      
      onLearningProgress?.(progress);
    }
  }, [isActive, readingSpeed, comprehensionScore, timeSpent, sectionsCompleted, userId, articleId, currentDifficulty, onLearningProgress]);

  const generateAdaptiveSuggestions = (): string[] => {
    const suggestions: string[] = [];
    
    if (comprehensionScore < 70) {
      suggestions.push('Consider reading more slowly');
      suggestions.push('Take breaks between sections');
      suggestions.push('Use the glossary for difficult terms');
    } else if (comprehensionScore > 90) {
      suggestions.push('Try the advanced difficulty level');
      suggestions.push('Explore related articles');
      suggestions.push('Share insights with others');
    }
    
    if (readingSpeed < 200) {
      suggestions.push('Practice speed reading techniques');
      suggestions.push('Use the focus mode for better concentration');
    } else if (readingSpeed > 400) {
      suggestions.push('Slow down to improve comprehension');
      suggestions.push('Take notes while reading');
    }
    
    return suggestions;
  };

  const generateComprehensionQuestion = useCallback((): ComprehensionQuestion => {
    // In a real app, this would use AI to generate questions based on content
    const sampleQuestions: ComprehensionQuestion[] = [
      {
        id: 'q1',
        question: 'What is the main topic of this article?',
        options: ['Technology', 'Science', 'Business', 'Education'],
        correctAnswer: 0,
        explanation: 'The article primarily discusses technological advancements and their impact.',
        difficulty: 'easy',
        category: 'main-idea'
      },
      {
        id: 'q2',
        question: 'Which of the following is mentioned as a key benefit?',
        options: ['Cost reduction', 'Time savings', 'Improved quality', 'All of the above'],
        correctAnswer: 3,
        explanation: 'The article mentions all three benefits as key advantages.',
        difficulty: 'medium',
        category: 'details'
      },
      {
        id: 'q3',
        question: 'What is the author\'s primary argument?',
        options: ['Technology is harmful', 'Technology is beneficial', 'Technology is neutral', 'Technology is complex'],
        correctAnswer: 3,
        explanation: 'The author presents a nuanced view that technology has both benefits and challenges.',
        difficulty: 'hard',
        category: 'analysis'
      }
    ];
    
    return sampleQuestions[Math.floor(Math.random() * sampleQuestions.length)];
  }, []);

  const handleStartLearning = () => {
    setIsActive(true);
    setTimeSpent(0);
    setSectionsCompleted(0);
    setComprehensionScore(0);
    setError(null);
  };

  const handleStopLearning = () => {
    setIsActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const handleComprehensionCheck = () => {
    const question = generateComprehensionQuestion();
    setCurrentQuestion(question);
    setShowComprehensionCheck(true);
    setSelectedAnswer(null);
    setShowExplanation(false);
  };

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null || !currentQuestion) return;
    
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const pointsEarned = isCorrect ? 10 : 0;
    
    setComprehensionScore(prev => Math.min(prev + pointsEarned, 100));
    setShowExplanation(true);
    
    onComprehensionCheck?.(currentQuestion);
  };

  const handleNextQuestion = () => {
    setShowComprehensionCheck(false);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setShowExplanation(false);
  };

  const handleSectionComplete = () => {
    setSectionsCompleted(prev => Math.min(prev + 1, totalSections));
  };

  const handleDifficultyChange = (difficulty: 'beginner' | 'intermediate' | 'advanced') => {
    setCurrentDifficulty(difficulty);
    
    // Adapt content based on difficulty
    let adaptedContent = content;
    
    switch (difficulty) {
      case 'beginner':
        // Simplify language, add explanations
        adaptedContent = content + '\n\n[Beginner Mode: Simplified explanations added]';
        break;
      case 'advanced':
        // Add complex concepts, remove simplifications
        adaptedContent = content + '\n\n[Advanced Mode: Additional technical details included]';
        break;
    }
    
    onContentAdapt?.(adaptedContent);
  };

  const formatTime = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 dark:text-green-400';
      case 'intermediate': return 'text-yellow-600 dark:text-yellow-400';
      case 'advanced': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getDifficultyBgColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 dark:bg-green-900';
      case 'intermediate': return 'bg-yellow-100 dark:bg-yellow-900';
      case 'advanced': return 'bg-red-100 dark:bg-red-900';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Learning Mode
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Adaptive reading experience that adjusts to your comprehension level
        </p>
      </div>

      {/* Learning Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={isActive ? handleStopLearning : handleStartLearning}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                isActive 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {isActive ? (
                <>
                  <Pause className="h-4 w-4 inline mr-2" />
                  Stop Learning
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 inline mr-2" />
                  Start Learning
                </>
              )}
            </button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Difficulty:</span>
              <select
                value={currentDifficulty}
                onChange={(e) => handleDifficultyChange(e.target.value as any)}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatTime(timeSpent)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Time Spent</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {comprehensionScore}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Comprehension</div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Brain className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Reading Speed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {readingSpeed} WPM
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Comprehension</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {comprehensionScore}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Clock className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Time Spent</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatTime(timeSpent)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <CheckCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Progress</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {sectionsCompleted}/{totalSections}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Adaptive Suggestions */}
      {adaptiveSuggestions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            <Wand className="h-5 w-5 inline mr-2" />
            Adaptive Suggestions
          </h3>
          <div className="space-y-2">
            {adaptiveSuggestions.map((suggestion, index) => (
              <div key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Sparkle className="h-4 w-4 mr-2 text-yellow-500" />
                {suggestion}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comprehension Check */}
      {showComprehensionCheck && currentQuestion && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Comprehension Check
          </h3>
          
          <div className="mb-4">
            <p className="text-gray-900 dark:text-white mb-4">{currentQuestion.question}</p>
            
            <div className="space-y-2">
              {currentQuestion.options.map((option, index) => (
                <label key={index} className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    name="answer"
                    value={index}
                    checked={selectedAnswer === index}
                    onChange={() => handleAnswerSelect(index)}
                    className="mr-3 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-900 dark:text-white">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {showExplanation && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Explanation:</h4>
              <p className="text-blue-800 dark:text-blue-200">{currentQuestion.explanation}</p>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={handleSubmitAnswer}
              disabled={selectedAnswer === null}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Answer
            </button>
            <button
              onClick={handleNextQuestion}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              Next Question
            </button>
          </div>
        </div>
      )}

      {/* Learning Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Learning Actions
        </h3>
        
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleComprehensionCheck}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
          >
            <Target className="h-4 w-4 mr-2" />
            Comprehension Check
          </button>
          
          <button
            onClick={handleSectionComplete}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark Section Complete
          </button>
          
          <button
            onClick={() => setReadingSpeed(prev => Math.min(prev + 25, 500))}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Increase Speed
          </button>
          
          <button
            onClick={() => setReadingSpeed(prev => Math.max(prev - 25, 100))}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
          >
            <TrendingDown className="h-4 w-4 mr-2" />
            Decrease Speed
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900 rounded-lg">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}
    </div>
  );
};

export default LearningMode;